import PropTypes from "prop-types";
import { usd } from "../../../util/formatCurrency.js";
import FavouriteButton from "./FavouriteButton.jsx";
import s from "./Hero.module.css";
import { LiveIcon, PersonalizeIcon, ShieldCheckIcon } from "./Icons.jsx";
import { Skeleton } from "../../../themeV2";

export default function Hero({
  avatarUrl,
  bannerUrl,
  caption,
  hasLivestreamProducts,
  influencerId,
  isLoading,
  name,
  priceMax,
  priceMin,
  productCount,
  vaCount,
}) {
  return (
    <section
      className={s.hero}
      style={{ backgroundImage: `url(${bannerUrl})` }}
    >
      <div className={s.section}>
        <div className={s.content}>
          {isLoading ? (
            <>
              <Skeleton
                variant="circle"
                tone="onDark"
                className={s.avatar}
                style={{ width: 64, height: 64, flexShrink: 0 }}
              />
              <div
                className={s.text}
                style={{ display: "flex", flexDirection: "column", gap: 8 }}
              >
                <Skeleton
                  variant="text"
                  size="xl"
                  tone="onDark"
                  style={{ width: 200 }}
                />
                <Skeleton variant="text" tone="onDark" style={{ width: 280 }} />
                <Skeleton variant="text" tone="onDark" style={{ width: 180 }} />
                <Skeleton
                  variant="rect"
                  tone="onDark"
                  style={{ width: 160, height: 40, borderRadius: 28 }}
                />
                <div style={{ display: "flex", gap: 16, marginTop: 4 }}>
                  <Skeleton
                    variant="text"
                    tone="onDark"
                    style={{ width: 80 }}
                  />
                  <Skeleton
                    variant="text"
                    tone="onDark"
                    style={{ width: 80 }}
                  />
                  <Skeleton
                    variant="text"
                    tone="onDark"
                    style={{ width: 100 }}
                  />
                </div>
              </div>
            </>
          ) : (
            <>
              <img className={s.avatar} src={avatarUrl} alt={name} />
              <div className={s.text}>
                <div className={s.titleRow}>
                  <h1>{name}</h1>
                  <FavouriteButton
                    influencerId={influencerId}
                    variant="light"
                  />
                </div>
                {caption && <p className={s.caption}>{caption}</p>}
                {!isLoading && (
                  <p className={s.valueProp}>
                    {hasLivestreamProducts
                      ? "Personalized autographs, signed live on stream"
                      : "Personalized autographs"}
                  </p>
                )}
                {productCount > 0 && (
                  <a href="#shop-products" className={s.heroCta}>
                    Browse Products
                  </a>
                )}
                {(vaCount > 0 || productCount > 0) && (
                  <div className={s.stats}>
                    {vaCount > 0 && (
                      <div className={s.stat}>
                        <span className={s.statValue}>{vaCount}+</span>
                        <span className={s.statLabel}>Talent</span>
                      </div>
                    )}
                    {productCount > 0 && (
                      <div className={s.stat}>
                        <span className={s.statValue}>{productCount}+</span>
                        <span className={s.statLabel}>Products</span>
                      </div>
                    )}
                    {priceMin != null && priceMax != null && (
                      <div className={s.stat}>
                        <span className={s.statValue}>
                          {usd(priceMin)}–{usd(priceMax)}
                        </span>
                        <span className={s.statLabel}>Price Range</span>
                      </div>
                    )}
                  </div>
                )}
                {!isLoading && (
                  <div className={s.trustSignals}>
                    {hasLivestreamProducts && (
                      <span className={s.trustItem}>
                        <LiveIcon />
                        Signed live on stream
                      </span>
                    )}
                    <span className={s.trustItem}>
                      <PersonalizeIcon />
                      Personalized to you
                    </span>
                    <span className={s.trustItem}>
                      <ShieldCheckIcon />
                      Authenticity guaranteed
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}

Hero.propTypes = {
  isLoading: PropTypes.bool,
  name: PropTypes.string,
  caption: PropTypes.string,
  avatarUrl: PropTypes.string,
  bannerUrl: PropTypes.string,
  hasLivestreamProducts: PropTypes.bool,
  vaCount: PropTypes.number,
  productCount: PropTypes.number,
  priceMin: PropTypes.number,
  priceMax: PropTypes.number,
  influencerId: PropTypes.number,
};
